import { ActionIcon, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useExportImage } from '@/hooks/useExportImage';
import type { RefObject } from 'react';

interface ExportButtonProps {
  /** Ref to the DOM element to capture */
  targetRef: RefObject<HTMLElement | null>;
  /** Name of the chart (used for filename and notification) */
  chartName: string;
  /** Optional size for the icon button */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Icon button that exports a referenced DOM element as a branded PNG.
 */
export function ExportButton({ targetRef, chartName, size = 'sm' }: ExportButtonProps) {
  const { exportElement, isExporting } = useExportImage();

  const handleExport = async () => {
    if (!targetRef.current) return;

    try {
      await exportElement(targetRef.current, chartName);
      notifications.show({
        title: 'Chart exported',
        message: `${chartName} saved as PNG`,
        color: 'green',
        autoClose: 3000,
      });
    } catch {
      notifications.show({
        title: 'Export failed',
        message: 'Could not export chart. Please try again.',
        color: 'red',
      });
    }
  };

  return (
    <Tooltip label={`Export ${chartName} as PNG`} position="left" withArrow>
      <ActionIcon
        variant="subtle"
        color="gray"
        size={size}
        onClick={handleExport}
        loading={isExporting}
        aria-label={`Export ${chartName}`}
      >
        <IconDownload size={16} />
      </ActionIcon>
    </Tooltip>
  );
}
